import type {Textile, TextileCreation} from "#material-outputs-publishing/targets/configura/client"

/**
 * Composition and verification of a Configura textile creation — the `noMatch` branch of the first
 * bind, once `createIfMissing` allows it. Pure functions, like the binding guards: the Material Database
 * write itself stays in the caller, which orders it last because it cannot be rolled back.
 */

/**
 * Defaults for every required creation field the platform holds no value for, verified accepted by
 * a live create (2026-08-27): the honest-empty forms — `""`, `[]`, `railroadDir: "N"` (None) — say
 * "nothing here" without asserting anything. The eight numeric fields have no such form; `1` is
 * the only known-accepted value and reads as a fact, so replacing it needs the customer's sign-off
 * or a "not specified" convention from Configura. `repeatH` / `repeatV` stay junk while open
 * question (d) (unit and source of the repeat fields) stands — the first publish after it settles
 * writes real values.
 */
export const TEXTILE_CREATION_DEFAULTS = {
    discontinued: false,
    category: "",
    pattern: "",
    weight: "",
    railroadDir: "N",
    width: "",
    finish: "",
    backing: "",
    durability: "",
    colorFastness: "",
    application: "",
    subCategory: [],
    content: [],
    cleaning: [],
    flameResist: [],
    pillingResist: 1,
    seamWarp: 1,
    seamFill: 1,
    tensileWarp: 1,
    tensileFill: 1,
    price: 1,
    repeatH: 1,
    repeatV: 1,
} satisfies Omit<TextileCreation, "companyId" | "sku" | "name">

/**
 * The four values the platform actually holds: the article ID becomes the immutable `sku`, the
 * material name becomes `name` (falling back to the article ID — the Material Database viewer shows a
 * nameless entry as blank), the material's range — its MaterialRange tag, the collection that
 * groups colourways — becomes `pattern`, which is what that field means in textile vocabulary,
 * and the company comes from the organization's profile, never inferred from the key. A material
 * can carry several range tags; reducing them to one is the caller's concern, this function takes
 * the chosen name.
 */
export function buildTextileCreation(args: {
    articleId: string
    materialName: string | null
    materialRangeName: string | null
    companyId: string
}): TextileCreation {
    const {articleId, materialName, materialRangeName, companyId} = args
    return {
        ...TEXTILE_CREATION_DEFAULTS,
        companyId,
        sku: articleId,
        name: materialName?.trim() || articleId,
        pattern: materialRangeName?.trim() || "",
    }
}

export type CreatedTextileCheckFailure =
    | {kind: "skuMismatch"; sku: string; returnedSku: string}
    | {kind: "companyMismatch"; sku: string; expectedCompanyId: string; actualCompanyId: string}

export type CreatedTextileCheckResult = {ok: true} | {ok: false; failure: CreatedTextileCheckFailure}

/**
 * The post-create re-check, in the binding-guard idiom: independent of the Material Database's implementation,
 * so a firing can only mean a stated field was ignored. Runs over a fresh `getTextile` of the
 * returned id — the create response carries only `{id, version}`.
 */
export function checkCreatedTextile(fetched: Textile, expected: {sku: string; companyId: string}): CreatedTextileCheckResult {
    if (fetched.sku !== expected.sku) {
        return {ok: false, failure: {kind: "skuMismatch", sku: expected.sku, returnedSku: fetched.sku}}
    }
    if (fetched.company.id !== expected.companyId) {
        return {ok: false, failure: {kind: "companyMismatch", sku: expected.sku, expectedCompanyId: expected.companyId, actualCompanyId: fetched.company.id}}
    }
    return {ok: true}
}

export function describeCreatedTextileCheckFailure(failure: CreatedTextileCheckFailure): string {
    switch (failure.kind) {
        case "skuMismatch":
            return `The Material Database created a textile with sku "${failure.returnedSku}" where "${failure.sku}" was requested — refusing to bind to it.`
        case "companyMismatch":
            return `The created textile with sku "${failure.sku}" landed in company ${failure.actualCompanyId}, not the stated company ${failure.expectedCompanyId} — refusing to bind to it.`
    }
}
